// web/src/components/TicketDetailBody.tsx —— 工单详情内容体（入口）：取数与状态编排 + 组合。
// 展示分片见 web/src/components/TicketDetailBody/ 目录（基本信息 / 业务字段 / 正文 / 页签区 / 底部操作条 / 四个弹层）。
// 既有调用方 import 路径与导出名不变；新增子块请落在对应分片，不要在此堆代码。

import { lazy, Suspense, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

// 懒加载通话详情抽屉：与 CallDetailDrawer 相互引用（其中又内嵌本组件），
// 用动态 import 切断静态循环依赖；仅在点击「查看呼叫记录」时才加载该 chunk。
const CallDetailDrawer = lazy(() => import('./CallDetailDrawer'));
import { Alert, Empty, Message, Space } from '@arco-design/web-react';
import TicketBasicInfoPanel from './TicketDetailBody/TicketBasicInfoPanel';
import TicketFieldsPanel from './TicketDetailBody/TicketFieldsPanel';
import TicketTextPanel from './TicketDetailBody/TicketTextPanel';
import TicketDetailTabs from './TicketDetailBody/TicketDetailTabs';
import TicketOpsBar from './TicketDetailBody/TicketOpsBar';
import TicketAttachmentPreviewModal from './TicketDetailBody/TicketAttachmentPreviewModal';
import TicketSwitchWorkflowModal from './TicketDetailBody/TicketSwitchWorkflowModal';
import TicketAssignModal from './TicketDetailBody/TicketAssignModal';
import TicketLinkModal from './TicketDetailBody/TicketLinkModal';
import {
  addTicketComment,
  assignTicket,
  deleteTicketAttachment,
  escalateTicket,
  getTicket,
  getVocabTree,
  linkTicket,
  listTicketAttachments,
  submitTicketWorkflow,
  switchTicketWorkflow,
  updateTicket,
  uploadTicketAttachment,
  urgeTicket,
  type Ticket,
  type TicketAttachment,
  type VocabTreeNode,
} from '../api/ticket';
import {
  getWorkflowInstance,
  listSelectableTemplates,
  type WorkflowInstance,
  type WorkflowTemplate,
} from '../api/workflow';
import { listUserOptions, type UserOption } from '../api/system';
import { useAuthStore } from '../store/auth';
import { useIntentNames } from '../utils/intentName';
import { msg } from '../utils/msg';

/** 关联编号（自增主键）只接受正整数：0 与负数都不是合法编号，清空视为未填。 */
const idOrUndefined = (v?: number) =>
  typeof v === 'number' && Number.isInteger(v) && v > 0 ? v : undefined;

export interface TicketDetailBodyProps {
  /** 要展示详情的工单 id（null/undefined 不加载） */
  ticketId?: number | null;
  onClose: () => void;
  /** 详情内写操作（状态流转/转派/提级/切换流程/提交审批）完成后回调，供父页刷新列表 */
  onChanged?: () => void;
  /** 操作区开关（默认 true）；只读场景（如待办中心查看）可传 false 隐藏底部操作区 */
  showOps?: boolean;
  /** 详情加载后回传标题（薄壳抽屉标题用，如「工单 #424 [AI自动]故障」） */
  onTitle?: (title: string) => void;
  /**
   * 底部操作条挂载目标（仅侧滑抽屉传抽屉 footer 容器）：
   * 就绪后操作条以 Portal 方式渲染到该节点——正文随抽屉滚动、操作条固定在抽屉底部；
   * 缺省在内容流末尾原位渲染（无滚动固定场景）。
   */
  opsTarget?: HTMLElement | null;
  /** 操作条是否处于可见态（有 detail 且 showOps 且 status）上报，供抽屉决定是否出现 footer 挂载区 */
  onOpsActive?: (active: boolean) => void;
}

/**
 * 工单详情内容体（TicketDetailDrawer 与待办中心流程抽屉共用，保证两处详情完全一致）：
 * 基本信息全字段 + 沟通评论 + 附件（上传/预览/删除）+ 关联 + 底部操作区（提交审批/状态流转/更多）+ 弹层。
 */
export default function TicketDetailBody({
  ticketId,
  onClose,
  onChanged,
  showOps = true,
  onTitle,
  opsTarget,
  onOpsActive,
}: TicketDetailBodyProps) {
  useIntentNames();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [detail, setDetail] = useState<Ticket | null>(null);
  const [wfInst, setWfInst] = useState<WorkflowInstance | null>(null);
  // 详情加载失败（工单不存在 / 无权查看 → 404）：与「加载中」区分，展示明确空态而非空白
  const [loadFailed, setLoadFailed] = useState(false);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [comment, setComment] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  // 操作权口径与后端 ticket.CanManage 一致：创建人/指派人/admin/supervisor。
  // 流程任务处理人只放开查看（待办中心以 showOps=false 只读打开详情），不在此列。
  const canOperate =
    !!detail &&
    (user?.role === 'admin' || user?.role === 'supervisor' ||
      detail.creator_id === user?.id || detail.assignee_id === user?.id);

  // 「提交审批」的可见判据：**无在途流程实例**（批次 A：建单不再发起流程，所以建完就该出现这个入口）。
  // 指针存在而实例尚未加载完成时按"有在途"处理（fail-closed，避免按钮闪一下又消失）；
  // 实例已是终态（finished/rejected/cancelled）但指针残留时放行——否则这类历史数据会卡在没有入口的状态，
  // 后端 SubmitWorkflow 按同一判据（实例 state=pending 才拒）兜底。
  const hasInflightFlow =
    !!detail?.workflow_instance_id && !['finished', 'rejected', 'cancelled'].includes(wfInst?.state || '');

  // 转派
  const [assignVisible, setAssignVisible] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assigneeId, setAssigneeId] = useState<number | undefined>();
  const [users, setUsers] = useState<UserOption[]>([]);

  // v1.11 多流程转接：切换审核流程
  const [switchVisible, setSwitchVisible] = useState(false);
  const [switchSaving, setSwitchSaving] = useState(false);
  const [switchTemplateId, setSwitchTemplateId] = useState<number | undefined>();
  const [workflows, setWorkflows] = useState<WorkflowTemplate[]>([]);

  // 关联通话/会话
  const [linkVisible, setLinkVisible] = useState(false);
  const [linkCdr, setLinkCdr] = useState<number>();
  const [linkChat, setLinkChat] = useState<number>();

  // 附件内嵌预览（本页面展示，不跳新标签页）
  const [previewAtt, setPreviewAtt] = useState<TicketAttachment | null>(null);

  // 关联通话 CDR 详情抽屉：在右侧再叠一层抽屉展示呼叫记录（不跳转呼叫记录页）
  const [cdrDetailId, setCdrDetailId] = useState<number | null>(null);

  // 工单分类树：详情含 cascade 字段时加载，用于把落库的分类 code 还原为分类名
  const [categoryTree, setCategoryTree] = useState<VocabTreeNode[]>([]);

  useEffect(() => {
    if (!ticketId) {
      setDetail(null);
      setWfInst(null);
      setAttachments([]);
      setCdrDetailId(null);
      setLoadFailed(false);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setWfInst(null);
    setAttachments([]);
    setLoadFailed(false);
    // v1.11 切换流程弹窗数据源（详情页亦加载一次，保证打开即有选项）
    listSelectableTemplates('ticket')
      .then(setWorkflows)
      .catch(() => setWorkflows([]));
    getTicket(ticketId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        onTitle?.(`工单 #${d?.id ?? ticketId} ${d?.title ?? ''}`.trim());
        // 已发起审核流程的工单：拉取实例状态（待办/通知联动展示）
        const instId = d?.workflow_instance_id;
        if (instId) {
          getWorkflowInstance(instId)
            .then((inst) => {
              if (!cancelled) setWfInst(inst);
            })
            .catch(() => {
              if (!cancelled) setWfInst(null);
            });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetail(null);
          setLoadFailed(true);
        }
      });
    listTicketAttachments(ticketId)
      .then((list) => {
        if (!cancelled) setAttachments(list || []);
      })
      .catch(() => {
        if (!cancelled) setAttachments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  // 详情含 cascade 类型字段时加载分类树（详情展示要把分类 code 还原为分类名）；无该类型字段不发请求
  useEffect(() => {
    if (!(detail?.field_defs || []).some((f) => f.type === 'cascade')) {
      setCategoryTree([]);
      return;
    }
    getVocabTree()
      .then((t) => setCategoryTree(t || []))
      .catch(() => setCategoryTree([]));
  }, [detail]);

  // v1.5 P6：催办/提级
  const doUrge = async () => {
    if (!detail) return;
    try {
      await urgeTicket(detail.id);
      Message.success('已催办（通知指派人）');
    } catch { /* 拦截器已提示 */ }
  };
  const doEscalate = async () => {
    if (!detail) return;
    try {
      await escalateTicket(detail.id);
      Message.success('已提级为紧急（SLA 已重算）');
      const d = await getTicket(detail.id);
      if (d) setDetail(d);
    } catch { /* 拦截器已提示 */ }
  };

  // v1.11 多流程转接：切换审核流程（取消在途实例 → 用新模板重新发起审批）
  const doSwitchWorkflow = async () => {
    if (!detail || !switchTemplateId) {
      Message.warning('请选择新审核流程');
      return;
    }
    setSwitchSaving(true);
    try {
      await switchTicketWorkflow(detail.id, switchTemplateId);
      Message.success('已切换审核流程并重新发起审批');
      setSwitchVisible(false);
      onChanged?.();
      onClose();
    } catch { /* 拦截器已提示（失败提示一元化） */ } finally {
      setSwitchSaving(false);
    }
  };

  // 附件
  const doUpload = async (file: File) => {
    if (!detail) return;
    try {
      await uploadTicketAttachment(detail.id, file);
      Message.success('附件已上传');
      setAttachments(await listTicketAttachments(detail.id));
    } catch { /* 拦截器已提示（失败提示一元化） */ }
    return false;
  };
  const doDeleteAttachment = async (att: TicketAttachment) => {
    if (!detail) return;
    try {
      await deleteTicketAttachment(detail.id, att.id);
      msg.deleted();
      setAttachments(await listTicketAttachments(detail.id));
    } catch { /* 拦截器已提示 */ }
  };

  // 关联通话/会话：编号非法（0/负数/小数）时不发请求，就地给可读提示（后端同样按 400 兜底）
  const doLink = async () => {
    if (!detail) return;
    const cdr = idOrUndefined(linkCdr);
    const chat = idOrUndefined(linkChat);
    if ((linkCdr !== undefined && !cdr) || (linkChat !== undefined && !chat)) {
      Message.warning('通话 CDR id 与在线会话 id 只能是大于 0 的整数');
      return;
    }
    if (!cdr && !chat) {
      Message.warning('请填写通话 CDR id 或在线会话 id');
      return;
    }
    try {
      await linkTicket(detail.id, { cdr_id: cdr, chat_session_id: chat });
      Message.success('已关联');
      setLinkVisible(false);
      setLinkCdr(undefined);
      setLinkChat(undefined);
      const d = await getTicket(detail.id);
      if (d) setDetail(d);
    } catch { /* 拦截器已提示 */ }
  };

  const onAddComment = async () => {
    const content = comment.trim();
    if (!content || !detail) return;
    setCommentSaving(true);
    try {
      await addTicketComment(detail.id, content);
      setComment('');
      const d = await getTicket(detail.id);
      if (d) setDetail(d);
      Message.success('评论已添加');
    } catch { /* 拦截器已提示 */ } finally {
      setCommentSaving(false);
    }
  };

  const onFlow = async (next: string, label: string) => {
    if (!detail) return;
    try {
      await updateTicket(detail.id, { status: next });
      Message.success(`操作成功：${label}`);
      // 刷新详情（resolved_at/closed_at 等字段由后端回填）
      try {
        const d = await getTicket(detail.id);
        if (d) setDetail(d);
      } catch {
        setDetail((prev) => (prev ? { ...prev, status: next } : prev));
      }
      onChanged?.();
    } catch { /* 拦截器已提示 */ }
  };

  const openAssign = async () => {
    if (!detail) return;
    setAssigneeId(detail.assignee_id ?? undefined);
    setAssignVisible(true);
    try {
      // 派单选人只需 id/name：走精简选人端点（无 PII、A 档登录即可），不读整表用户
      setUsers(await listUserOptions());
    } catch { /* noop */ }
  };

  const onAssign = async () => {
    if (!detail || !assigneeId) {
      Message.warning('请选择转派人');
      return;
    }
    setAssignSaving(true);
    try {
      await assignTicket(detail.id, assigneeId);
      Message.success('已转派');
      setAssignVisible(false);
      const name = users.find((u) => u.id === assigneeId)?.name;
      setDetail((prev) => (prev ? { ...prev, assignee_id: assigneeId, assignee_name: name || prev.assignee_name } : prev));
      onChanged?.();
    } catch { /* 拦截器已提示 */ } finally {
      setAssignSaving(false);
    }
  };

  const doSubmitWorkflow = async () => {
    if (!detail) return;
    try {
      await submitTicketWorkflow(detail.id);
      Message.success('已提交审批流程，审批全部通过后工单自动完成');
      onChanged?.();
      onClose();
    } catch { /* 拦截器已提示（失败提示一元化） */ }
  };

  // 操作条可见态上报：抽屉据此挂载/卸载底部 footer 挂载区，避免空 footer 白条
  useEffect(() => {
    onOpsActive?.(!!(showOps && detail?.status));
  }, [showOps, detail?.status, onOpsActive]);

  // 底部操作条（提交审批/状态流转 + 更多）：抽屉场景经 Portal 渲染到 drawer footer 固定不动，
  // 正文内容随抽屉滚动；无 opsTarget（非抽屉内嵌）则原位渲染在内容末尾
  const opsNode =
    showOps && detail?.status ? (
      <TicketOpsBar
        detail={detail}
        canOperate={canOperate}
        hasInflightFlow={hasInflightFlow}
        opsTarget={opsTarget}
        onSubmitWorkflow={doSubmitWorkflow}
        onFlow={onFlow}
        onOpenAssign={openAssign}
        onOpenSwitch={() => {
          setSwitchTemplateId(detail.workflow_template_id ?? undefined);
          setSwitchVisible(true);
        }}
        onUrge={doUrge}
        onEscalate={doEscalate}
        onOpenLink={() => setLinkVisible(true)}
      />
    ) : null;

  return (
    <>
      {!detail && loadFailed && <Empty description="该工单不存在或无权查看" />}
      {detail && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {!canOperate && (
            <Alert
              type="warning"
              content="当前账号仅可查看该工单；提交审批、切换流程、转派、催办、提级、状态流转等操作仅限工单创建人或指派人（主管/管理员不受限）"
            />
          )}
          <TicketBasicInfoPanel detail={detail} />
          <TicketFieldsPanel detail={detail} categoryTree={categoryTree} />
          <TicketTextPanel content={detail.content} description={detail.description} />
          <TicketDetailTabs
            detail={detail}
            wfInst={wfInst}
            comment={comment}
            commentSaving={commentSaving}
            onCommentChange={setComment}
            onAddComment={onAddComment}
            attachments={attachments}
            onUpload={doUpload}
            onDeleteAttachment={doDeleteAttachment}
            onPreview={setPreviewAtt}
            onOpenCdr={(cdrId) => setCdrDetailId(cdrId)}
            onOpenOutbound={(taskId) => navigate(`/outbound?id=${taskId}`)}
            onOpenWorkflow={() => navigate('/workflow')}
            onOpenLink={() => setLinkVisible(true)}
          />

          {opsTarget ? (opsNode ? createPortal(opsNode, opsTarget) : null) : opsNode}
        </Space>
      )}

      {/* 附件预览弹窗（本页模态框，按 mime 分流展示；不支持的类型引导下载） */}
      <TicketAttachmentPreviewModal detail={detail} previewAtt={previewAtt} onClose={() => setPreviewAtt(null)} />

      {/* v1.11 多流程转接：切换审核流程（取消在途实例 → 用新模板重新发起审批） */}
      <TicketSwitchWorkflowModal
        detail={detail}
        visible={switchVisible}
        saving={switchSaving}
        templateId={switchTemplateId}
        onTemplateChange={setSwitchTemplateId}
        workflows={workflows}
        onOk={doSwitchWorkflow}
        onCancel={() => setSwitchVisible(false)}
      />

      <TicketAssignModal
        detail={detail}
        visible={assignVisible}
        saving={assignSaving}
        assigneeId={assigneeId}
        onAssigneeChange={setAssigneeId}
        users={users}
        onOk={onAssign}
        onCancel={() => setAssignVisible(false)}
      />

      {/* 关联通话/会话（v1.5 P6） */}
      <TicketLinkModal
        detail={detail}
        visible={linkVisible}
        linkCdr={linkCdr}
        linkChat={linkChat}
        onCdrChange={setLinkCdr}
        onChatChange={setLinkChat}
        onOk={doLink}
        onCancel={() => setLinkVisible(false)}
      />

      {/* 关联通话 CDR 详情抽屉：在工单详情右侧再叠一层展示呼叫记录（不跳转呼叫记录页） */}
      <Suspense fallback={null}>
        <CallDetailDrawer
          visible={!!cdrDetailId}
          cdrId={cdrDetailId}
          onClose={() => setCdrDetailId(null)}
        />
      </Suspense>
    </>
  );
}
